import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { environment } from 'src/environments/environment';
import { RecaptchaComponent } from 'ng-recaptcha';
import { ToastrService } from 'ngx-toastr';
import { EnrollmentService } from 'src/app/Services/enrollment.service';
import { LevelService } from 'src/app/Services/level.service';

/** Modalidad del nivel; decide qué secciones y campos se muestran. */
export type LevelKey = 'inicial' | 'primario' | 'secundario' | 'terciario';

/** `kind` de la API (Level::KINDS) mapeado a la modalidad usada acá. */
const KIND_TO_KEY: { [kind: string]: LevelKey } = {
  initial: 'inicial',
  primary: 'primario',
  secondary: 'secundario',
  tertiary: 'terciario',
};

/**
 * Respaldo por nombre para niveles que todavía no tienen `kind` cargado en el
 * panel. Sin modalidad el backend valida el nivel como no-terciario y descarta
 * los datos del aspirante (CUA-129), así que conviene no depender sólo de ella.
 */
const NAME_TO_KEY: Array<{ needle: string; key: LevelKey }> = [
  { needle: 'inicial', key: 'inicial' },
  { needle: 'primari', key: 'primario' },
  { needle: 'secundari', key: 'secundario' },
  { needle: 'superior', key: 'terciario' },
  { needle: 'terciari', key: 'terciario' },
];

/** Sections used to group fields and render their headings. */
type SectionId = 'student' | 'father' | 'mother' | 'tertiary';

type FieldType = 'text' | 'date' | 'email' | 'tel' | 'boolean' | 'textarea';

interface EnrollmentLevel {
  /** `level_id` enviado en POST /public/enrollments */
  id: number;
  name: string;
  /** Modalidad resuelta desde `kind` (o desde el nombre como respaldo) */
  key: LevelKey | null;
}

interface EnrollmentSection {
  id: SectionId;
  title: string;
  /** Levels in which this whole section is shown */
  levels: LevelKey[];
}

interface EnrollmentField {
  /** API payload property (POST /public/enrollments) */
  key: string;
  label: string;
  type: FieldType;
  section: SectionId;
  half?: boolean;
  placeholder?: string;
  /** Levels in which the field is mandatory */
  requiredFor?: LevelKey[];
  /** Only show this field when another boolean field equals the given value */
  showWhen?: { key: string; equals: boolean };
  /**
   * The document captures this field but the API has no matching property.
   * Its value is folded into `observations` on submit instead of being sent
   * as its own key. Used for "Título de nivel medio".
   */
  foldIntoObservations?: boolean;
}

@Component({
  selector: 'app-sblock-enrollment-form',
  templateUrl: './enrollment-form.component.html',
  styleUrls: ['./enrollment-form.component.scss'],
})
export class SblockEnrollmentFormComponent implements OnInit, OnChanges {
  // ===== Heading / theme (mirrors app-sblock-form) =====
  showTitles = true;
  title = 'Formulario de Pre-inscripción';
  subtitle = 'Completá los datos del aspirante según el nivel al que se inscribe.';
  alignTextBlock = 'center';
  titlesColor = '#2B2A6B';
  secondaryColor = '#27A7C2';
  buttonText = 'Enviar pre-inscripción';
  levelSectionTitle = 'Nivel al que se inscribe';

  // ===== reCAPTCHA (same setup as the contact form) =====
  recaptchaSiteKey =
    (environment as any).recaptchaSiteKey || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
  recaptchaToken: string | null = null;
  @ViewChild(RecaptchaComponent) captchaRef?: RecaptchaComponent;

  /**
   * Modalidades que el formulario deja elegir. La página de inscripciones la
   * usa para que cada tab ofrezca sólo sus niveles (CUA-133); vacío = todas.
   */
  @Input() levelKeys: LevelKey[] = [];

  /** Niveles de GET /public/levels; vacío hasta que responda la API */
  levels: EnrollmentLevel[] = [];

  /** Nivel elegido; su `id` es el `level_id` que recibe la API */
  selectedLevelId: number | null = null;

  readonly sections: EnrollmentSection[] = [
    { id: 'student', title: 'Datos del Alumno/a', levels: ['inicial', 'primario', 'secundario'] },
    { id: 'father', title: 'Datos del Padre', levels: ['inicial', 'primario', 'secundario'] },
    { id: 'mother', title: 'Datos de la Madre', levels: ['inicial', 'primario', 'secundario'] },
    { id: 'tertiary', title: 'Datos del Aspirante', levels: ['terciario'] },
  ];

  /**
   * Field catalogue. Visibility is driven by each field's section (which in turn
   * is filtered by the selected level) plus the optional `showWhen` condition.
   */
  readonly fields: EnrollmentField[] = [
    // ---- Inicial / Primario / Secundario · Alumno (Ficha de datos personales) ----
    { key: 'student_last_name', label: 'Apellido', type: 'text', section: 'student', half: true, requiredFor: ['inicial', 'primario', 'secundario'] },
    { key: 'student_first_name', label: 'Nombre', type: 'text', section: 'student', half: true, requiredFor: ['inicial', 'primario', 'secundario'] },
    { key: 'student_dni', label: 'DNI', type: 'text', section: 'student', half: true, requiredFor: ['inicial', 'primario', 'secundario'] },
    { key: 'student_birth_date', label: 'Fecha de nacimiento', type: 'date', section: 'student', half: true, requiredFor: ['inicial', 'primario', 'secundario'] },
    { key: 'student_birth_place', label: 'Lugar de nacimiento', type: 'text', section: 'student' },
    { key: 'student_address', label: 'Dirección', type: 'text', section: 'student', half: true },
    { key: 'student_neighborhood', label: 'Barrio', type: 'text', section: 'student', half: true },
    { key: 'grade_applying_for', label: 'Sala / grado / año al que se inscribe', type: 'text', section: 'student', requiredFor: ['inicial', 'primario', 'secundario'] },
    { key: 'previous_school', label: 'Jardín / Escuela de la que proviene', type: 'text', section: 'student' },
    { key: 'has_external_support', label: '¿Ha tenido o tiene acompañamiento profesional externo?', type: 'boolean', section: 'student' },
    { key: 'external_support_details', label: 'Especificá cuál o cuáles', type: 'text', section: 'student', showWhen: { key: 'has_external_support', equals: true } },
    { key: 'has_siblings_in_school', label: '¿Tiene hermanos en el Colegio?', type: 'boolean', section: 'student', half: true },
    { key: 'siblings_level', label: '¿En qué nivel?', type: 'text', section: 'student', half: true, showWhen: { key: 'has_siblings_in_school', equals: true } },

    // ---- Datos del Padre ----
    { key: 'father_last_name', label: 'Apellido', type: 'text', section: 'father', half: true },
    { key: 'father_first_name', label: 'Nombre', type: 'text', section: 'father', half: true },
    { key: 'father_dni', label: 'D.N.I.', type: 'text', section: 'father', half: true },
    { key: 'father_birth_date', label: 'Fecha de nacimiento', type: 'date', section: 'father', half: true },
    { key: 'father_birth_place', label: 'Lugar de nacimiento', type: 'text', section: 'father', half: true },
    { key: 'father_address', label: 'Dirección', type: 'text', section: 'father', half: true },
    { key: 'father_neighborhood', label: 'Barrio', type: 'text', section: 'father', half: true },
    { key: 'father_phone', label: 'Teléfono / Celular', type: 'tel', section: 'father', half: true },
    { key: 'father_email', label: 'Dirección de correo', type: 'email', section: 'father', half: true },
    { key: 'father_occupation', label: 'Ocupación', type: 'text', section: 'father', half: true },
    { key: 'father_marital_status', label: 'Estado civil', type: 'text', section: 'father', half: true },
    { key: 'father_is_former_student', label: '¿Ex-alumno?', type: 'boolean', section: 'father', half: true },

    // ---- Datos de la Madre ----
    { key: 'mother_last_name', label: 'Apellido', type: 'text', section: 'mother', half: true },
    { key: 'mother_first_name', label: 'Nombre', type: 'text', section: 'mother', half: true },
    { key: 'mother_dni', label: 'D.N.I.', type: 'text', section: 'mother', half: true },
    { key: 'mother_birth_date', label: 'Fecha de nacimiento', type: 'date', section: 'mother', half: true },
    { key: 'mother_birth_place', label: 'Lugar de nacimiento', type: 'text', section: 'mother', half: true },
    { key: 'mother_address', label: 'Dirección', type: 'text', section: 'mother', half: true },
    { key: 'mother_neighborhood', label: 'Barrio', type: 'text', section: 'mother', half: true },
    { key: 'mother_phone', label: 'Teléfono / Celular', type: 'tel', section: 'mother', half: true },
    { key: 'mother_email', label: 'Dirección de correo', type: 'email', section: 'mother', half: true },
    { key: 'mother_occupation', label: 'Ocupación', type: 'text', section: 'mother', half: true },
    { key: 'mother_marital_status', label: 'Estado civil', type: 'text', section: 'mother', half: true },
    { key: 'mother_is_former_student', label: '¿Ex-alumna?', type: 'boolean', section: 'mother', half: true },

    // ---- Terciario · Aspirante (Solicitud de Inscripción) ----
    { key: 'career', label: 'Carrera', type: 'text', section: 'tertiary', requiredFor: ['terciario'] },
    { key: 'student_last_name', label: 'Apellido', type: 'text', section: 'tertiary', half: true, requiredFor: ['terciario'] },
    { key: 'student_first_name', label: 'Nombre', type: 'text', section: 'tertiary', half: true, requiredFor: ['terciario'] },
    { key: 'student_dni', label: 'D.N.I.', type: 'text', section: 'tertiary', half: true, requiredFor: ['terciario'] },
    { key: 'student_birth_date', label: 'Fecha de nacimiento', type: 'date', section: 'tertiary', half: true, requiredFor: ['terciario'] },
    { key: 'student_birth_place', label: 'Lugar de nacimiento / Provincia', type: 'text', section: 'tertiary', half: true },
    { key: 'civil_status', label: 'Estado civil', type: 'text', section: 'tertiary', half: true },
    { key: 'student_address', label: 'Domicilio', type: 'text', section: 'tertiary', half: true },
    { key: 'student_neighborhood', label: 'Localidad', type: 'text', section: 'tertiary', half: true },
    { key: 'student_phone', label: 'Teléfono', type: 'tel', section: 'tertiary', half: true },
    { key: 'student_email', label: 'E-mail', type: 'email', section: 'tertiary', half: true, requiredFor: ['terciario'] },
    { key: 'secondary_school_title', label: 'Título de nivel medio', type: 'text', section: 'tertiary', half: true, foldIntoObservations: true },
    { key: 'title_issued_by', label: 'Extendido por', type: 'text', section: 'tertiary', half: true },
    { key: 'graduation_year', label: 'Año de egreso', type: 'text', section: 'tertiary', half: true },
    { key: 'workplace', label: 'Lugar de trabajo', type: 'text', section: 'tertiary', half: true },
    { key: 'observations', label: 'Observaciones', type: 'textarea', section: 'tertiary' },
  ];

  /** Field values keyed by the API property name */
  model: { [key: string]: any } = {};

  submitting = false;
  /** Shows the static success message after a submission, until a new level is picked */
  submittedSuccessfully = false;
  /** Inline field errors are shown only after the first submit attempt */
  submitAttempted = false;

  private readonly emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private readonly dniRe = /^\d{7,8}$/;
  private readonly phoneRe = /^[\d\s()+-]{7,20}$/;

  constructor(
    private enrollmentService: EnrollmentService,
    private levelService: LevelService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadLevels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['levelKeys']) {
      this.applyLevelFilter();
    }
  }

  /** Los niveles y sus ids salen de la API; hardcodearlos desincroniza el `level_id` */
  private async loadLevels(): Promise<void> {
    const res = await this.levelService.getLevels();
    const list = res?.data?.data;
    if (!Array.isArray(list)) {
      return;
    }
    this.levels = list
      .filter(level => level && level.id != null)
      .map(level => ({
        id: Number(level.id),
        name: level.name,
        key: this.resolveLevelKey(level),
      }));
    this.applyLevelFilter();
  }

  /** Modalidad del nivel: primero `kind`, y si viene vacío se deduce del nombre */
  private resolveLevelKey(level: any): LevelKey | null {
    const kind = (level.kind || '').toLowerCase();
    if (KIND_TO_KEY[kind]) {
      return KIND_TO_KEY[kind];
    }
    const name = (level.name || '').toLowerCase();
    return NAME_TO_KEY.find(entry => name.includes(entry.needle))?.key || null;
  }

  /** Niveles ofrecidos por el filtro `levelKeys`; sin filtro, todos los de la API */
  get visibleLevels(): EnrollmentLevel[] {
    if (!this.levelKeys || !this.levelKeys.length) {
      return this.levels;
    }
    return this.levels.filter(level => !!level.key && this.levelKeys.indexOf(level.key as LevelKey) !== -1);
  }

  /**
   * Mantiene la selección dentro de los niveles ofrecidos: descarta el elegido
   * si el filtro lo dejó afuera y, cuando queda una sola opción, la elige para
   * no pedir un paso de más.
   */
  private applyLevelFilter(): void {
    const visible = this.visibleLevels;
    if (this.selectedLevelId !== null && visible.some(level => level.id === this.selectedLevelId)) {
      return;
    }
    this.selectedLevelId = visible.length === 1 ? visible[0].id : null;
    this.onLevelChange();
  }

  get selectedLevel(): EnrollmentLevel | null {
    return this.levels.find(l => l.id === this.selectedLevelId) || null;
  }

  /** Modalidad del nivel elegido (drives which sections/fields are shown) */
  get selectedLevelKey(): LevelKey | null {
    return this.selectedLevel?.key || null;
  }

  /** Sections visible for the currently selected level */
  get visibleSections(): EnrollmentSection[] {
    if (!this.selectedLevelKey) {
      return [];
    }
    return this.sections.filter(s => s.levels.includes(this.selectedLevelKey as LevelKey));
  }

  /**
   * Whether the selected level renders parent sections. When it does, the API
   * requires at least one parent name, so the UI enforces the same minimum.
   */
  get levelHasParents(): boolean {
    return this.visibleSections.some(s => s.id === 'father' || s.id === 'mother');
  }

  /** Fields of a section, honouring conditional (`showWhen`) visibility */
  fieldsForSection(sectionId: SectionId): EnrollmentField[] {
    return this.fields.filter(f => f.section === sectionId && this.isFieldShown(f));
  }

  isFieldShown(field: EnrollmentField): boolean {
    if (!field.showWhen) {
      return true;
    }
    return this.model[field.showWhen.key] === field.showWhen.equals;
  }

  /**
   * Obligatorio sólo si el nivel elegido figura en el `requiredFor` del campo,
   * de modo que el formulario exija lo mismo que StoreEnrollmentRequest y no
   * más (CUA-130). El mínimo de un padre o madre se valida aparte, en onSubmit.
   */
  isRequired(field: EnrollmentField): boolean {
    const levelKey = this.selectedLevelKey;
    if (!levelKey) {
      return false;
    }
    return !!field.requiredFor && field.requiredFor.includes(levelKey);
  }

  /** When the level changes, drop values that don't belong to the new level */
  onLevelChange(): void {
    // Picking a level means starting a new request: clear the success message
    this.submittedSuccessfully = false;
    this.submitAttempted = false;
    const allowedSections = new Set(this.visibleSections.map(s => s.id));
    this.fields.forEach(field => {
      if (!allowedSections.has(field.section)) {
        delete this.model[field.key];
      }
    });
  }

  onCaptchaResolved(token: string | null): void {
    this.recaptchaToken = token;
  }

  /** All fields currently visible for the selected level */
  private currentFields(): EnrollmentField[] {
    const allowed = new Set(this.visibleSections.map(s => s.id));
    return this.fields.filter(f => allowed.has(f.section) && this.isFieldShown(f));
  }

  private isEmpty(value: any): boolean {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  }

  /** Build the POST /public/enrollments body from the visible, filled fields */
  private buildPayload(): { [key: string]: any } {
    const level = this.selectedLevel;
    const payload: { [key: string]: any } = { level_id: level?.id };

    const observationLines: string[] = [];

    this.currentFields().forEach(field => {
      const value = this.model[field.key];
      if (this.isEmpty(value)) {
        return;
      }
      if (field.foldIntoObservations) {
        observationLines.push(`${field.label}: ${String(value).trim()}`);
        return;
      }
      payload[field.key] = field.type === 'text' || field.type === 'email' || field.type === 'tel'
        ? String(value).trim()
        : value;
    });

    // Fold the document-only "Título de nivel medio" into observations
    if (observationLines.length) {
      const existing = payload.observations ? [payload.observations] : [];
      payload.observations = existing.concat(observationLines).join('\n');
    }

    return payload;
  }

  /** Validation message for a field, or null when it's valid */
  fieldError(field: EnrollmentField): string | null {
    const value = this.model[field.key];
    const empty = this.isEmpty(value);

    if (this.isRequired(field) && empty) {
      return 'Este campo es obligatorio.';
    }
    if (empty) {
      return null; // optional and empty → nothing to validate
    }

    const str = String(value).trim();

    if (field.key.endsWith('_dni')) {
      return this.dniRe.test(str) ? null : 'Ingresá un DNI válido (7 u 8 dígitos, sin puntos).';
    }
    if (field.type === 'email') {
      return this.emailRe.test(str) ? null : 'Ingresá un correo electrónico válido.';
    }
    if (field.type === 'tel') {
      return this.phoneRe.test(str) ? null : 'Ingresá un teléfono válido.';
    }
    if (field.type === 'date' && field.key.includes('birth_date')) {
      const time = new Date(str).getTime();
      if (!isNaN(time) && time > Date.now()) {
        return 'La fecha de nacimiento no puede ser futura.';
      }
    }
    if (field.key === 'graduation_year' && !/^\d{4}$/.test(str)) {
      return 'Ingresá un año válido (4 dígitos).';
    }
    return null;
  }

  /** First visible field that fails validation (required or format) */
  private firstInvalidField(): { field: EnrollmentField; error: string } | null {
    for (const field of this.currentFields()) {
      const error = this.fieldError(field);
      if (error) {
        return { field, error };
      }
    }
    return null;
  }

  /** Placeholder for a field; type-aware default when none is configured */
  placeholderFor(field: EnrollmentField): string {
    if (field.placeholder) {
      return field.placeholder;
    }
    if (field.type === 'date' || field.type === 'boolean') {
      return '';
    }
    if (field.key.endsWith('_dni')) {
      return 'Ej: 12345678';
    }
    if (field.type === 'email') {
      return 'ejemplo@correo.com';
    }
    if (field.type === 'tel') {
      return 'Ej: +54 9 342 1234567';
    }
    return `Ingresá ${field.label.toLowerCase()}`;
  }

  /**
   * Pull a user-readable message out of an HttpErrorResponse. The API answers
   * validation problems with `{ message, errors: { field: [msg, ...] } }`;
   * prefer the first field error, then the top-level message.
   */
  private apiErrorMessage(error: any): string | null {
    const body = error?.error;
    if (!body) {
      return null;
    }
    if (body.errors && typeof body.errors === 'object') {
      const first = Object.values(body.errors)[0];
      if (Array.isArray(first) && first.length) {
        return String(first[0]);
      }
    }
    return typeof body.message === 'string' ? body.message : null;
  }

  async onSubmit(): Promise<void> {
    if (this.submitting) {
      return;
    }
    this.submitAttempted = true;
    if (!this.selectedLevelId) {
      this.toastr.warning('Seleccioná el nivel al que se inscribe.');
      return;
    }
    if (!this.selectedLevelKey) {
      this.toastr.warning('El nivel seleccionado no tiene modalidad configurada. Contactanos para completar tu inscripción.');
      return;
    }
    const invalid = this.firstInvalidField();
    if (invalid) {
      this.toastr.warning(`${invalid.field.label}: ${invalid.error}`);
      return;
    }
    if (this.levelHasParents && this.isEmpty(this.model['father_first_name']) && this.isEmpty(this.model['mother_first_name'])) {
      this.toastr.warning('Ingresá al menos el nombre del padre o de la madre.');
      return;
    }
    // Espeja el `required_with` del backend: cargado el nombre, el apellido deja de ser opcional
    if (this.levelHasParents) {
      const missingLastName = [
        { first: 'father_first_name', last: 'father_last_name', label: 'del padre' },
        { first: 'mother_first_name', last: 'mother_last_name', label: 'de la madre' },
      ].find(p => !this.isEmpty(this.model[p.first]) && this.isEmpty(this.model[p.last]));

      if (missingLastName) {
        this.toastr.warning(`Ingresá también el apellido ${missingLastName.label}.`);
        return;
      }
    }
    if (!this.recaptchaToken) {
      this.toastr.warning('Confirmá que no sos un robot.');
      return;
    }

    this.submitting = true;
    try {
      await this.enrollmentService.createEnrollment(this.buildPayload());
      this.toastr.success('Inscripción enviada correctamente.');
      this.resetForm();
      this.submittedSuccessfully = true;
    } catch (error) {
      this.toastr.error(this.apiErrorMessage(error) || 'No se pudo enviar la inscripción. Intentá nuevamente.');
    } finally {
      this.submitting = false;
    }
  }

  private resetForm(): void {
    this.model = {};
    this.selectedLevelId = null;
    this.recaptchaToken = null;
    this.submitAttempted = false;
    this.captchaRef?.reset();
  }
}
